import React, { memo, useEffect, useRef, useState } from 'react';

// PDF drawn page by page inside the platform (no toolbar of the browser, hence no download
// button). The library is loaded the first time a PDF is opened.
let pdfjsReady = null;
const loadPdfjs = () => {
  if (!pdfjsReady) {
    pdfjsReady = import('pdfjs-dist/legacy/build/pdf').then((pdfjs) => {
      pdfjs.GlobalWorkerOptions.workerSrc = new URL('pdfjs-dist/legacy/build/pdf.worker.min.js', import.meta.url).toString();
      return pdfjs;
    });
    pdfjsReady.catch(() => {
      pdfjsReady = null;
    });
  }
  return pdfjsReady;
};

// A page is drawn when it comes near the screen, then kept.
const Page = memo(({ pdf, number, width, ratio }) => {
  const holder = useRef(null);
  const canvas = useRef(null);
  const [near, setNear] = useState(false);
  const [drawn, setDrawn] = useState(false);

  useEffect(() => {
    const node = holder.current;
    if (!node || near) return undefined;
    if (!('IntersectionObserver' in window)) {
      setNear(true);
      return undefined;
    }
    const observer = new IntersectionObserver(
      (entries) => {
        if (entries.some((entry) => entry.isIntersecting)) {
          setNear(true);
          observer.disconnect();
        }
      },
      { rootMargin: '1200px 0px' }
    );
    observer.observe(node);
    return () => observer.disconnect();
  }, [near]);

  useEffect(() => {
    if (!near || !width) return undefined;
    let task = null;
    let cancelled = false;
    pdf
      .getPage(number)
      .then((page) => {
        if (cancelled || !canvas.current) return null;
        const base = page.getViewport({ scale: 1 });
        const pixels = Math.min(window.devicePixelRatio || 1, 2);
        const viewport = page.getViewport({ scale: (width / base.width) * pixels });
        const target = canvas.current;
        target.width = Math.floor(viewport.width);
        target.height = Math.floor(viewport.height);
        target.style.width = `${width}px`;
        target.style.height = `${viewport.height / pixels}px`;
        task = page.render({ canvasContext: target.getContext('2d'), viewport });
        return task.promise.then(() => !cancelled && setDrawn(true));
      })
      .catch(() => {});
    return () => {
      cancelled = true;
      if (task) task.cancel();
    };
  }, [pdf, number, near, width]);

  return (
    <div
      ref={holder}
      className="relative mx-auto overflow-hidden bg-white shadow-[0_2px_12px_rgba(0,0,0,0.18)]"
      style={{ width, minHeight: drawn ? undefined : Math.round(width * ratio) }}
    >
      <canvas ref={canvas} aria-label={`Page ${number}`} className="block" />
    </div>
  );
});

const PdfPages = ({ blob, zoom = 1, onError, onPages }) => {
  const box = useRef(null);
  const [pdf, setPdf] = useState(null);
  const [ratio, setRatio] = useState(1.414);
  const [boxWidth, setBoxWidth] = useState(0);

  useEffect(() => {
    let cancelled = false;
    let loaded = null;
    setPdf(null);
    Promise.all([loadPdfjs(), blob.arrayBuffer()])
      .then(([pdfjs, data]) => pdfjs.getDocument({ data: new Uint8Array(data), isEvalSupported: false }).promise)
      .then(async (document) => {
        loaded = document;
        const first = await document.getPage(1);
        const viewport = first.getViewport({ scale: 1 });
        if (cancelled) return;
        setRatio(viewport.height / viewport.width);
        setPdf(document);
        if (onPages) onPages(document.numPages);
      })
      .catch(() => !cancelled && onError && onError());
    return () => {
      cancelled = true;
      if (loaded) loaded.destroy();
    };
    // A new blob means a new document; the callbacks do not.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [blob]);

  useEffect(() => {
    const node = box.current;
    if (!node) return undefined;
    const measure = () => setBoxWidth(node.clientWidth);
    measure();
    if (!('ResizeObserver' in window)) return undefined;
    const observer = new ResizeObserver(measure);
    observer.observe(node);
    return () => observer.disconnect();
  }, []);

  const fitWidth = Math.max(Math.min(boxWidth - 32, 920), 200);
  const width = Math.round(fitWidth * zoom);

  return (
    <div ref={box} className="w-full">
      {!pdf ? (
        <p className="m-0 py-10 text-center text-[14px] text-white/70">Préparation du document…</p>
      ) : (
        <div className="flex flex-col items-center gap-[16px] px-[16px] py-[20px]" style={{ minWidth: width + 32 }}>
          {Array.from({ length: pdf.numPages }, (_, index) => (
            <Page key={index + 1} pdf={pdf} number={index + 1} width={width} ratio={ratio} />
          ))}
        </div>
      )}
    </div>
  );
};

export default PdfPages;
